import React, { useMemo, useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import "echarts-liquidfill";

const MAX_VALUE = 1000;

export default function DualGauge({ H1 = 0, H2 = 0, H1_FY, H2_FY }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const percent1 = Math.min(1, Math.max(0, H1 / MAX_VALUE));
  const percent2 = Math.min(1, Math.max(0, H2 / MAX_VALUE));

  const blueGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: "rgba(147, 197, 253, 0.7)" },
    { offset: 1, color: "rgba(29, 78, 216, 0.7)" },
  ]);

  const greenGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: "#6ee7b7" },
    { offset: 1, color: "#047857" },
  ]);

  const option = useMemo(() => {
    // Desktop config
    let center1 = ["25%", "45%"];
    let center2 = ["75%", "45%"];
    let radius = "80%";
    let valueFont = 36;
    let titleFont = 16;
    let h1FYLeft = "18%";
    let h2FYLeft = "70%";

    // Mobile config (side-by-side, no overlap)
    if (isMobile) {
      center1 = ["22%", "45%"];
      center2 = ["78%", "45%"];
      radius = "80%";
      valueFont = 26;
      titleFont = 13;
      h1FYLeft = "14%";
      h2FYLeft = "70%";
    }

    return {
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const actualValue = params.seriesName === "H1" ? H1 : H2;
          return `${params.seriesName} : ${actualValue}`;
        },
      },

      series: [
        {
          type: "liquidFill",
          name: "H1",
          data: [percent1, percent1 * 0.97, percent1 * 0.94],
          center: center1,
          radius,
          color: [blueGradient],
          backgroundStyle: {
            color: "#F8FAFC",
          },
          outline: {
            show: true,
            borderDistance: 0,
            itemStyle: {
              borderWidth: isMobile ? 4 : 5,
              borderColor: "#CBD5E1",
            },
          },
          label: {
            show: true,
            position: "inside",
            formatter: () => `{value|${H1}}\n{title|H1}`,
            rich: {
              value: {
                fontSize: valueFont,
                fontWeight: 700,
                color: "#FFFFFF",
                lineHeight: valueFont,
              },
              title: {
                fontSize: titleFont,
                fontWeight: 500,
                color: "#FFFFFF",
                padding: [8, 0, 0, 0],
              },
            },
          },
          waveAnimation: true,
          amplitude: 6,
          period: 10000,
        },

        {
          type: "liquidFill",
          name: "H2",
          data: [percent2, percent2 * 0.97, percent2 * 0.94],
          center: center2,
          radius,
          color: [greenGradient],
          backgroundStyle: {
            color: "#F8FAFC",
          },
          outline: {
            show: true,
            borderDistance: 0,
            itemStyle: {
              borderWidth: isMobile ? 4 : 5,
              borderColor: "#CBD5E1",
            },
          },
          label: {
            show: true,
            position: "inside",
            formatter: () => `{value|${H2}}\n{title|H2}`,
            rich: {
              value: {
                fontSize: valueFont,
                fontWeight: 700,
                color: "#FFFFFF",
                lineHeight: valueFont,
              },
              title: {
                fontSize: titleFont,
                fontWeight: 500,
                color: "#FFFFFF",
                padding: [8, 0, 0, 0],
              },
            },
          },
          waveAnimation: true,
          amplitude: 6,
          period: 10000,
        },
      ],

      graphic: [
        {
          type: "text",
          left: h1FYLeft,
          top: "88%",
          style: {
            text: H1_FY ? `FY ${H1_FY}` : "—",
            font: `${isMobile ? 11 : 12}px Uni Neue`,
            fill: "#64748B",
            textAlign: "center",
          },
        },
        {
          type: "text",
          left: h2FYLeft,
          top: "88%",
          style: {
            text: H2_FY ? `FY ${H2_FY}` : "—",
            font: `${isMobile ? 11 : 12}px Uni Neue`,
            fill: "#64748B",
            textAlign: "center",
          },
        },
      ],

      animation: true,
    };
  }, [isMobile, percent1, percent2, H1, H2, H1_FY, H2_FY]);
  return (
    <ReactECharts
      option={option}
      echarts={echarts}
      style={{
        width: "100%",
        height: isMobile ? 180 : 260,
      }}
    />
  );
}
